/**
 * The Admin → Notifications oversight switches (#981).
 *
 * The person each event is about is always notified by the producers; these
 * switches add oversight copies for organization admins and/or managers. The
 * UI is not the enforcement — `settings.notification_oversight` is read by
 * `services/assignment_notifications.py` on every event, and the PUT is
 * validated server-side (unknown event keys and non-boolean values are a 400).
 *
 * Load/persist shape copied from `AiPrivacyControls.jsx` (raw `axios`, so the
 * step-up interceptor applies). Save sends `notification_oversight` and
 * nothing else, so no other org setting can be touched from here (#981).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../lib/apiErrors';
import { isStepUpRequired, stepUpBlockedNotice, stepUpThenReload, STEP_UP_ACTION_LABEL } from '../../lib/stepUp';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** The five events, in page order — mirrors `services.assignment_notifications.EVENTS`. */
const EVENTS = [
    { key: 'contact_owner_assigned', label: 'Contact owner assigned', hint: 'A contact gets a new Contact Owner.' },
    { key: 'company_owner_assigned', label: 'Company owner assigned', hint: 'A company gets a new owner.' },
    { key: 'contact_coach_assigned', label: 'Contact coach assigned', hint: 'A contact gets a new Assigned Coach.' },
    { key: 'company_team_member_added', label: 'Company team member added', hint: "Someone is added to a company's team." },
    { key: 'lead_outcome', label: 'Lead reached an outcome stage', hint: 'A lead moves to a stage whose outcome is Won or Lost.' },
];

const ROLES = ['admins', 'managers'];

/** The stored map normalised to all five events; anything absent reads off. */
const normalise = (stored) => Object.fromEntries(EVENTS.map(({ key }) => (
    [key, { admins: stored?.[key]?.admins === true, managers: stored?.[key]?.managers === true }]
)));

export default function NotificationOversightSettings() {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [switches, setSwitches] = useState(() => normalise(undefined));

    const load = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        setBlocked(false);
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            setSwitches(normalise(res.data?.settings?.notification_oversight));
            setDirty(false);
        } catch (err) {
            if (isStepUpRequired(err)) setBlocked(true);
            else setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const toggle = (eventKey, role) => {
        setSwitches(prev => ({ ...prev, [eventKey]: { ...prev[eventKey], [role]: !prev[eventKey][role] } }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${API}/organizations/current`,
                // Only this key — never any other org setting (#981 must-keep).
                { settings: { notification_oversight: normalise(switches) } },
                { headers: getAuthHeader() },
            );
            toast.success('Notification settings saved');
            // Re-read the stored state from the response (the PUT returns the
            // org document, shaped by the backend's `_org_response`).
            setSwitches(normalise(res.data?.settings?.notification_oversight ?? switches));
            setDirty(false);
        } catch (err) {
            // The switches keep the unsaved state; the server message wins when
            // it names the problem (e.g. a validation 400).
            toast.error(errorText(err, 'Could not save notification settings'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-8 text-sm text-stone-500" aria-busy="true">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading notification settings…
            </div>
        );
    }

    if (blocked) {
        return (
            <div className="p-8 text-sm text-stone-500" data-testid="notif-oversight-blocked">
                <p>{stepUpBlockedNotice().text}</p>
                {stepUpBlockedNotice().actionable && (
                    <button
                        type="button"
                        onClick={() => stepUpThenReload()}
                        className="mt-3 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:bg-stone-50"
                    >
                        {STEP_UP_ACTION_LABEL}
                    </button>
                )}
            </div>
        );
    }

    if (loadFailed) {
        return (
            <div className="p-8 text-sm text-stone-500" data-testid="notif-oversight-error">
                <p>Could not load notification settings.</p>
                <button
                    type="button"
                    onClick={() => load()}
                    className="mt-3 px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:bg-stone-50"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="notif-oversight-page">
            <p className="text-sm text-stone-500">
                Choose which events also notify organization admins and managers. The person each
                event is about is always notified; these switches add oversight copies.
            </p>

            <div className="overflow-x-auto rounded-xl border border-stone-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-stone-200 text-left text-xs font-medium uppercase tracking-wide text-stone-500">
                            <th scope="col" className="px-4 py-3 font-medium">Event</th>
                            <th scope="col" className="px-4 py-3 font-medium w-[88px]">Admins</th>
                            <th scope="col" className="px-4 py-3 font-medium w-[88px]">Managers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {EVENTS.map(({ key, label, hint }) => (
                            <tr key={key} data-testid={`notif-oversight-row-${key}`} className="border-b border-stone-100 last:border-b-0">
                                <td className="px-4 py-3">
                                    <p className="text-stone-900">{label}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{hint}</p>
                                </td>
                                {ROLES.map(role => (
                                    <td key={role} className="px-4 py-3 align-middle">
                                        <Switch
                                            checked={switches[key][role]}
                                            onCheckedChange={() => toggle(key, role)}
                                            disabled={saving}
                                            aria-label={`${label} — ${role === 'admins' ? 'Admins' : 'Managers'}`}
                                            data-testid={`notif-oversight-${key}-${role}`}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-stone-500">
                    Admins and managers are only told about records they can see, and never about
                    their own changes.
                </p>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="notif-oversight-save"
                    className="shrink-0 px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> : null}
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    );
}
